import React, { useEffect, useMemo, useState } from 'react';
import QuestionCard from './QuestionCard';

/**
 * LeaderPick — cross-fixture leader question (DKPLAT-354).
 *
 * Two visual states:
 *   1. Collapsed card with "Select a [player|team] to get the most [stat]"
 *      placeholder + the user's current selection (if any).
 *   2. Bottom-sheet modal listing every candidate ranked by their live stat
 *      value. Default to top 30; "Show all" reveals the remainder.
 *
 * The component is presentational — the parent passes already-loaded
 * candidates with live `statValue` populated. Re-fetching / SSE plumbing
 * lives in the frontend factory adapter.
 */

export interface LeaderCandidate {
  /** Option ID — what gets stored as the user's selection */
  id: string;
  /** Display name (e.g. "Patrick Mahomes" or "Kansas City Chiefs") */
  name: string;
  /** Optional player position (e.g. "QB") — only for player_leader */
  position?: string;
  /** Team trigram (e.g. "KC") shown next to the name */
  teamShortcode?: string;
  /** Headshot / team logo URL */
  imageUrl?: string;
  /** Small team logo to show alongside the headshot */
  teamLogoUrl?: string;
  /** Fixture label, e.g. "ATL @ CIN" — useful when candidates span multiple games */
  fixtureLabel?: string;
  /** Matchup time, e.g. "Sun, 1:00 PM" */
  matchTime?: string;
  /** Current accumulated stat value across the contest window */
  statValue: number;
  /** Popularity percentage — share of other users who picked this option */
  popularityPct?: number;
}

export interface LeaderPickProps {
  /** Candidate list, in the order they should appear (already ranked by stat) */
  options: LeaderCandidate[];
  /** Stat label, e.g. "Passing Yards", shown in both card + modal */
  statLabel: string;
  /** Kind of leader question — controls placeholder copy + position-prefix in modal title */
  kind: 'player' | 'team';
  /** Currently selected option id, if any */
  selectedOptionId?: string;
  /** Called when the user picks an option in the modal */
  onSelect: (optionId: string | undefined) => void;
  /** How many options to show before "Show all" expands the list */
  defaultLimit?: number;
  /** Question metadata for QuestionCard styling */
  points: number;
  earnedPoints?: number;
  questionText?: string;
  subtext?: string;
  isCorrect?: boolean;
  disabled?: boolean;
  questionId?: string;
  questionNumber?: number;
  questionMetadata?: { dailyDouble?: boolean };
}

const PLACEHOLDER_PLAYER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"><circle cx="32" cy="22" r="11" stroke="#6F6F6F" stroke-width="3"/><path d="M10 56c0-12 10-20 22-20s22 8 22 20" stroke="#6F6F6F" stroke-width="3" stroke-linecap="round"/></svg>`
  );

// Generic team-shield silhouette — used when a candidate is a team but the
// feed didn't return a logo URL. Avoids the person icon misleading the
// customer into thinking team_leader questions are about a single player.
const PLACEHOLDER_TEAM =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"><path d="M32 6 L54 14 V32 C54 44 44 54 32 58 C20 54 10 44 10 32 V14 Z" stroke="#6F6F6F" stroke-width="3" stroke-linejoin="round"/></svg>`
  );

const placeholderFor = (kind: 'player' | 'team') =>
  kind === 'team' ? PLACEHOLDER_TEAM : PLACEHOLDER_PLAYER;

const LeaderPick: React.FC<LeaderPickProps> = ({
  options,
  statLabel,
  kind,
  selectedOptionId,
  onSelect,
  defaultLimit = 30,
  points,
  earnedPoints,
  questionText,
  subtext,
  isCorrect,
  disabled = false,
  questionId,
  questionNumber,
  questionMetadata,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const isDailyDouble = questionMetadata?.dailyDouble || false;

  // Lock background scroll while the modal is open so the page underneath
  // doesn't drift when the user scrolls inside the candidate list.
  useEffect(() => {
    if (!isModalOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isModalOpen]);

  const selected = useMemo(
    () => options.find(o => o.id === selectedOptionId),
    [options, selectedOptionId]
  );

  const visibleOptions = showAll ? options : options.slice(0, defaultLimit);

  const placeholderCopy =
    kind === 'team'
      ? `Select a team to get the most ${statLabel}`
      : `Select a player to get the most ${statLabel}`;

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : 'leader-pick'}
      data-testtype="leader"
    >
      {subtext && (
        <span
          data-testid="leader-subtext"
          className="text-[#ABABAB] font-light block text-center mt-2"
        >
          {subtext}
        </span>
      )}
      <span
        data-testid="leader-question"
        className="text-text-primary font-medium block text-center mb-4"
      >
        {questionText || placeholderCopy}
      </span>

      {/* Collapsed selection card */}
      <button
        type="button"
        onClick={() => !disabled && setIsModalOpen(true)}
        disabled={disabled}
        data-testid="leader-open-modal"
        className={`group w-full rounded-xl p-3 md:p-6 transition-smooth border ${
          isDailyDouble ? 'bg-[#000D1F] border-[#0D54AD]' : 'bg-[#121212] border-[#121212]'
        } ${disabled ? 'cursor-not-allowed opacity-80' : 'hover:border-[#6F6F6F]'} flex flex-col items-center gap-3`}
      >
        {selected ? (
          <>
            <img
              src={selected.imageUrl || placeholderFor(kind)}
              alt={selected.name}
              className="w-20 h-20 object-contain"
              onError={e => {
                e.currentTarget.src = placeholderFor(kind);
              }}
            />
            <div className="text-center">
              <div className="text-white text-sm font-semibold">{selected.name}</div>
              {(selected.position || selected.teamShortcode) && (
                <div className="text-[#ABABAB] text-xs mt-0.5">
                  {selected.position}
                  {selected.position && selected.teamShortcode && ' • '}
                  {selected.teamShortcode}
                </div>
              )}
              {/* Stat value intentionally omitted from the selected card —
                  the question is about the FINAL leader at end of contest,
                  so showing a running total here was confusing. The modal
                  still surfaces the live stat alongside each candidate. */}
            </div>
          </>
        ) : (
          <>
            <img
              src={placeholderFor(kind)}
              alt="Select"
              className="w-20 h-20 object-contain opacity-70"
            />
            <div className="text-white text-sm font-semibold">
              {kind === 'team' ? 'Select a team' : 'Select a player'}
            </div>
            <div className="text-[#ABABAB] text-xs">Tap to choose from {options.length}</div>
          </>
        )}
      </button>

      {/* Modal — rendered inline (no portal) so it stacks above this card without needing app-level setup */}
      {isModalOpen && (
        <div
          data-testid="leader-modal"
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-[#121212] border border-[#2A2A2A] rounded-t-2xl md:rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A]">
              <div>
                <div className="text-white text-sm">
                  Pick {questionNumber ? <span className="font-bold">{questionNumber}</span> : ''}
                </div>
                <div className="text-white font-semibold">
                  Select a {kind === 'team' ? 'team' : 'player'} to get the most {statLabel}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close"
                className="text-white text-2xl leading-none px-2"
              >
                ×
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-2 text-[#ABABAB] text-xs border-b border-[#2A2A2A]">
                <span>{kind === 'team' ? 'Team' : 'Player'}</span>
                <span>{statLabel}</span>
              </div>
              {visibleOptions.map(opt => {
                const isPicked = opt.id === selectedOptionId;
                return (
                  <button
                    type="button"
                    key={opt.id}
                    data-testid={`leader-option-${opt.id}`}
                    onClick={() => {
                      onSelect(isPicked ? undefined : opt.id);
                      setIsModalOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-[#1F1F1F] text-left transition-smooth ${
                      isPicked ? 'bg-[#1A1A1A]' : 'hover:bg-[#181818]'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={opt.imageUrl || placeholderFor(kind)}
                        alt={opt.name}
                        className="w-10 h-10 rounded-full object-cover bg-[#2A2A2A] flex-shrink-0"
                        onError={e => {
                          e.currentTarget.src = placeholderFor(kind);
                        }}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1 text-sm text-white">
                          {opt.teamLogoUrl && (
                            <img
                              src={opt.teamLogoUrl}
                              alt=""
                              className="w-3.5 h-3.5"
                              onError={e => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <span className="font-semibold truncate">{opt.name}</span>
                          {opt.position && (
                            <span className="text-[#ABABAB]">{opt.position}</span>
                          )}
                          {opt.teamShortcode && (
                            <span className="text-[#ABABAB]">• {opt.teamShortcode}</span>
                          )}
                        </div>
                        {(opt.fixtureLabel || opt.matchTime) && (
                          <div className="text-[#ABABAB] text-xs mt-0.5">
                            {opt.fixtureLabel}
                            {opt.fixtureLabel && opt.matchTime && '  '}
                            {opt.matchTime}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-white text-base font-semibold tabular-nums">
                      {opt.statValue.toLocaleString()}
                    </div>
                  </button>
                );
              })}
              {!showAll && options.length > defaultLimit && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  data-testid="leader-show-all"
                  className="w-full text-center text-[#ABABAB] text-sm py-3 hover:text-white"
                >
                  Show all ({options.length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </QuestionCard>
  );
};

export default LeaderPick;
